/**
 * Audio Context - Domain-Specific State Management
 * 
 * Manages audio system state across the application
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { audioSystemController } from '../core/audio/AudioSystemController';

interface AudioContextState {
  isInitialized: boolean;
  isPlaying: boolean;
  volume: number;
  sabianVolume: number;
  activeEngines: string[];
}

interface AudioContextValue extends AudioContextState {
  initialize: () => Promise<void>;
  setVolume: (volume: number) => void;
  setSabianVolume: (volume: number) => void;
  play: () => void;
  pause: () => void;
  resume: () => Promise<void>;
}

export const AudioContext = createContext<AudioContextValue | undefined>(undefined);

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AudioContextState>({
    isInitialized: false,
    isPlaying: false,
    volume: -20,
    sabianVolume: -20,
    activeEngines: []
  });

  useEffect(() => {
    // Subscribe to audio system state changes
    const unsubscribe = audioSystemController.onInitialized(() => {
      const systemState = audioSystemController.getState();
      setState(prev => ({
        ...prev,
        isInitialized: systemState.isInitialized,
        activeEngines: systemState.enginesReady
      }));
    });

    return unsubscribe;
  }, []);

  const initialize = async () => {
    await audioSystemController.initialize(true);
  };

  const setVolume = (volume: number) => {
    audioSystemController.setMainVolume(volume);
    setState(prev => ({ ...prev, volume }));
  };

  const setSabianVolume = (volume: number) => {
    audioSystemController.setSabianVolume(volume);
    setState(prev => ({ ...prev, sabianVolume: volume }));
  };

  const play = () => {
    setState(prev => ({ ...prev, isPlaying: true }));
  };

  const pause = () => {
    setState(prev => ({ ...prev, isPlaying: false }));
  };

  const resume = async () => {
    await audioSystemController.resume();
  };

  const value: AudioContextValue = {
    ...state,
    initialize,
    setVolume,
    setSabianVolume,
    play,
    pause,
    resume
  };

  return <AudioContext.Provider value={value}>{children}</AudioContext.Provider>;
};

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within AudioProvider');
  }
  return context;
};

